/**
 * controlledSubstances.ts
 *
 * Is this drug a controlled substance, and on which federal schedule?
 *
 * Partell's API (v2.2) makes day supply and ICD-10 optional on a prescription
 * and REQUIRED for controlled substances. The form used to demand both on
 * every line, which turned a vitamin injection into a script that asked for a
 * diagnosis it did not need. The vendor draws the line at the schedule, so the
 * form needs to know the schedule — from the drug NAME, because a script read
 * off a PDF is free text that never touched the catalogue.
 *
 * Matched by lowercase substring of the active ingredient against the name:
 * "Testosterone Cypionate 200mg/mL Vial" and "Testosterone | Estriol |
 * Estradiol Creams" are both Schedule III; "7-Keto DHEA" and "Ketorolac" are
 * not ketamine and not controlled. Salts, plurals and prefixed forms
 * ("oxycodone HCl", "amphetamines", "esketamine") still match.
 *
 * The list is the federal CSA schedule for the actives seen in the Partell
 * catalogue plus the common additions a compounding pharmacy is asked for.
 * Anything NOT listed is treated as NOT controlled: the vendor's own 400 is the
 * backstop for a scheduled drug we do not know, and a false negative costs one
 * bounced order, whereas a false positive blocks a script that needs no
 * diagnosis. DHEA, HCG, HGH (somatropin), the GLP-1s, sildenafil / tadalafil,
 * oxytocin and the peptides are deliberately absent — not federally scheduled,
 * however tightly some states watch them.
 *
 * MIRRORED in api/app/Integrations/RmmControlledSubstances.php. Keep the two
 * lists identical: this copy decides which fields the form insists on and the
 * PHP copy is what the API refuses with, and a drug the two disagree about is
 * a 422 with no field on screen to fix.
 */

export type ControlledSchedule = 'II' | 'III' | 'IV' | 'V';

/** Active-ingredient keyword (lowercase) → federal schedule. */
export const CONTROLLED_SCHEDULES: Readonly<Record<string, ControlledSchedule>> = {
  // Schedule II — opioids and stimulants.
  oxycodone: 'II',
  hydrocodone: 'II',
  hydromorphone: 'II',
  oxymorphone: 'II',
  morphine: 'II',
  fentanyl: 'II',
  methadone: 'II',
  tapentadol: 'II',
  meperidine: 'II',
  amphetamine: 'II',
  methamphetamine: 'II',
  lisdexamfetamine: 'II',
  methylphenidate: 'II',
  dexmethylphenidate: 'II',
  // Schedule III — anabolic steroids, ketamine (and so esketamine),
  // buprenorphine, dronabinol.
  testosterone: 'III',
  nandrolone: 'III',
  oxandrolone: 'III',
  stanozolol: 'III',
  ketamine: 'III',
  buprenorphine: 'III',
  dronabinol: 'III',
  // Schedule IV — benzodiazepines, Z-drugs, wakefulness agents, anorectics,
  // tramadol, carisoprodol, phenobarbital, butorphanol.
  alprazolam: 'IV',
  midazolam: 'IV',
  diazepam: 'IV',
  lorazepam: 'IV',
  clonazepam: 'IV',
  temazepam: 'IV',
  triazolam: 'IV',
  modafinil: 'IV',
  armodafinil: 'IV',
  phentermine: 'IV',
  diethylpropion: 'IV',
  zolpidem: 'IV',
  zaleplon: 'IV',
  eszopiclone: 'IV',
  tramadol: 'IV',
  carisoprodol: 'IV',
  phenobarbital: 'IV',
  butorphanol: 'IV',
  // Schedule V — the scheduled anticonvulsants.
  pregabalin: 'V',
  lacosamide: 'V',
  brivaracetam: 'V',
  cenobamate: 'V',
};

const RANK: Record<ControlledSchedule, number> = { II: 2, III: 3, IV: 4, V: 5 };

/**
 * The federal schedule this drug falls under, or null when it is not a
 * controlled substance we know of. A combination naming two actives gets the
 * most restrictive schedule — the one the pharmacy will hold the script to.
 */
export function controlledSchedule(name: string): ControlledSchedule | null {
  const normalised = name.toLowerCase().replace(/\s+/g, ' ').trim();
  if (normalised === '') return null;

  let best: ControlledSchedule | null = null;
  for (const [keyword, schedule] of Object.entries(CONTROLLED_SCHEDULES)) {
    if (!normalised.includes(keyword)) continue;
    if (best === null || RANK[schedule] < RANK[best]) best = schedule;
  }
  return best;
}

export function isControlledSubstance(name: string): boolean {
  return controlledSchedule(name) !== null;
}
